import React, { useState, useEffect } from 'react';
import { 
  Database, Save, Loader2, CheckCircle2, XCircle, 
  RefreshCw, ExternalLink, AlertCircle, Zap, ArrowRight, Edit3,
  ArrowLeftRight, Clock, Map, Route, Users, Truck, History,
  Download, Upload, Play, Pause, Settings, Activity, Calendar
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const TruckDatabaseConfigPanel = ({ user }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [editingMapping, setEditingMapping] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncHistory, setSyncHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('general');
  const [syncingType, setSyncingType] = useState(null);
  
  const [config, setConfig] = useState({
    enabled: false,
    api_url: '',
    api_key: '',
    sync_trigger_statuses: ['devis_accepte', 'planifiee'],
    sync_frequency_minutes: 0,
    retry_on_failure: true,
    max_retries: 3,
    status_mapping: {
      "devis_accepte": "Nouveau",
      "planifiee": "En attente",
      "prete_au_chargement": "En attente",
      "en_cours_de_chargement": "En chargement",
      "charge_en_route": "En cours",
      "livree": "Terminée",
      "terminee": "Terminée",
      "annulee_admin": "Annulée",
      "annulee_par_client": "Annulée"
    },
    // Configuration PUSH (Portail → Truck DB)
    push_config: {
      enabled: true,
      sync_mode: 'realtime', // 'realtime', 'batch', 'manual',
      batch_interval_minutes: 15,
      sync_on_create: true,
      sync_on_status_change: true,
      sync_on_update: false,
      include_fields: ['all'], // ou liste spécifique,
      exclude_fields: []
    },
    // Configuration PULL (Truck DB → Portail)
    pull_config: {
      enabled: true,
      sync_mode: 'delta', // 'full', 'delta', 'manual',
      cache_ttl_minutes: 5,
      full_sync_interval_hours: 24,
      sync_drivers: true,
      sync_vehicles: true,
      sync_enterprise_clients: true,
      sync_routes: true,
      auto_calculate_distance: true,
      distance_priority: 'osrm' // 'osrm', 'truck_db', 'mapbox'
    }
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const availableStatuses = [
    { value: 'devis_accepte', label: 'Devis accepté' },
    { value: 'planifiee', label: 'Planifiée' },
    { value: 'prete_au_chargement', label: 'Prête au chargement' },
    { value: 'en_cours_de_chargement', label: 'En cours de chargement' },
    { value: 'charge_en_route', label: 'En route' },
    { value: 'livree', label: 'Livrée' },
    { value: 'terminee', label: 'Terminée' },
    { value: 'annulee_admin', label: 'Annulée (admin)' },
    { value: 'annulee_par_client', label: 'Annulée (client)' }
  ];

  const statusLabels = {
    "devis_accepte": "Devis accepté",
    "planifiee": "Planifiée",
    "prete_au_chargement": "Prête au chargement",
    "en_cours_de_chargement": "En cours de chargement",
    "charge_en_route": "En route",
    "livree": "Livrée",
    "terminee": "Terminée",
    "annulee_admin": "Annulée (admin)",
    "annulee_par_client": "Annulée (client)"
  };

  useEffect(() => {
    fetchConfig();
    fetchSyncStatus();
    fetchSyncHistory();
  }, []);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/system-config/truck-database`, {
        headers: {
          'Authorization': 'Bearer cookie-auth'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setConfig(prev => ({ ...prev, ...data }));
      }
    } catch (error) {
      console.error('Error fetching config:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger la configuration',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchSyncStatus = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/external/status`, {
        headers: {
          'Authorization': 'Bearer cookie-auth'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSyncStatus(data);
      }
    } catch (error) {
      console.error('Error fetching sync status:', error);
    }
  };

  const fetchSyncHistory = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/external/sync-history?limit=10`, {
        headers: {
          'Authorization': 'Bearer cookie-auth'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSyncHistory(data.history || []);
      }
    } catch (error) {
      console.error('Error fetching sync history:', error);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/system-config/truck-database`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer cookie-auth'
        },
        body: JSON.stringify(config)
      });

      const data = await response.json();

      if (response.ok) {
        toast({
          title: 'Succès',
          description: 'Configuration enregistrée avec succès'
        });
        setTestResult(null);
      } else {
        throw new Error(data.detail || 'Erreur lors de la sauvegarde');
      }
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const response = await fetch(`${BACKEND_URL}/api/system-config/truck-database/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer cookie-auth'
        }
      });

      const data = await response.json();
      setTestResult(data);

      if (data.success) {
        toast({ title: 'Connexion réussie', description: data.message });
      } else {
        toast({ title: 'Échec de la connexion', description: data.error, variant: 'destructive' });
      }
    } catch (error) {
      setTestResult({ success: false, error: error.message });
      toast({ title: 'Erreur', description: error.message, variant: 'destructive' });
    } finally {
      setTesting(false);
    }
  };

  const triggerSync = async (type) => {
    setSyncingType(type);
    try {
      const response = await fetch(`${BACKEND_URL}/api/external/sync/${type}`, {
        method: 'POST',
        headers: { 'Authorization': 'Bearer cookie-auth' }
      });

      const data = await response.json();
      
      if (response.ok) {
        toast({
          title: 'Synchronisation terminée',
          description: `${data.count || data.drivers_count || data.vehicles_count || 0} éléments synchronisés`
        });
        fetchSyncStatus();
        fetchSyncHistory();
      } else {
        throw new Error(data.detail || 'Erreur de synchronisation');
      }
    } catch (error) {
      toast({ title: 'Erreur', description: error.message, variant: 'destructive' });
    } finally {
      setSyncingType(null);
    }
  };

  const handleStatusToggle = (status) => {
    const current = config.sync_trigger_statuses || [];
    if (current.includes(status)) {
      setConfig({ ...config, sync_trigger_statuses: current.filter(s => s !== status) });
    } else {
      setConfig({ ...config, sync_trigger_statuses: [...current, status] });
    }
  };

  const handleMappingChange = (customerStatus, truckDbStatus) => {
    setConfig({
      ...config,
      status_mapping: { ...config.status_mapping, [customerStatus]: truckDbStatus }
    });
    setEditingMapping(null);
  };

  const updatePushConfig = (key, value) => {
    setConfig({
      ...config,
      push_config: { ...config.push_config, [key]: value }
    });
  };

  const updatePullConfig = (key, value) => {
    setConfig({
      ...config,
      pull_config: { ...config.pull_config, [key]: value }
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Database className="w-6 h-6 text-blue-600 mt-0.5" />
          <div className="flex-1">
            <h4 className="font-semibold text-blue-900">Synchronisation Truck Database</h4>
            <p className="text-sm text-blue-700 mt-1">
              Configuration bidirectionnelle complète : Portail Client ↔ Truck Database
            </p>
          </div>
          {syncStatus?.configured && (
            <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full flex items-center gap-1">
              <Activity className="w-3 h-3" />
              Connecté
            </span>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b overflow-x-auto">
        {[
          { id: 'general', label: 'Général', icon: Settings },
          { id: 'push', label: 'PUSH (→ Truck DB)', icon: Upload },
          { id: 'pull', label: 'PULL (← Truck DB)', icon: Download },
          { id: 'distances', label: 'Distances & GPS', icon: Route },
          { id: 'history', label: 'Historique', icon: History }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === tab.id
                ? 'border-blue-600 text-blue-600 bg-blue-50/50'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab: General */}
      {activeTab === 'general' && (
        <div className="space-y-6">
          {/* Enable/Disable */}
          <div className="flex items-center justify-between p-4 bg-white border rounded-lg">
            <div>
              <h4 className="font-medium text-gray-900">Activer la synchronisation</h4>
              <p className="text-sm text-gray-500">
                Activer l'intégration bidirectionnelle avec Truck Database
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={config.enabled}
                onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          {/* API Configuration */}
          <div className="border rounded-lg p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Zap className="w-5 h-5 text-yellow-500" />
              Configuration de l'API
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  URL de l'API Truck Database *
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="https://truck-database.example.com"
                  value={config.api_url}
                  onChange={(e) => setConfig({ ...config, api_url: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Clé API *
                </label>
                <input
                  type="password"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="votre-cle-api-secrete"
                  value={config.api_key}
                  onChange={(e) => setConfig({ ...config, api_key: e.target.value })}
                />
              </div>
            </div>

            {testResult && (
              <div className={`p-4 rounded-lg flex items-start gap-3 ${
                testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
              }`}>
                {testResult.success ? (
                  <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
                )}
                <div>
                  <p className={`font-medium ${testResult.success ? 'text-green-900' : 'text-red-900'}`}>
                    {testResult.success ? 'Connexion réussie' : 'Échec de la connexion'}
                  </p>
                  <p className={`text-sm ${testResult.success ? 'text-green-700' : 'text-red-700'}`}>
                    {testResult.success ? testResult.message : testResult.error}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Retry Options */}
          <div className="border rounded-lg p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Options de résilience</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">Réessayer en cas d'échec</p>
                  <p className="text-sm text-gray-500">Relancer automatiquement les syncs échouées</p>
                </div>
                <input
                  type="checkbox"
                  className="w-5 h-5 text-blue-600 rounded"
                  checked={config.retry_on_failure}
                  onChange={(e) => setConfig({ ...config, retry_on_failure: e.target.checked })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre max de tentatives
                </label>
                <input
                  type="number"
                  min={1}
                  max={10}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  value={config.max_retries}
                  onChange={(e) => setConfig({ ...config, max_retries: parseInt(e.target.value) })}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab: PUSH (Portal to Truck DB) */}
      {activeTab === 'push' && (
        <div className="space-y-6">
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <h4 className="font-semibold text-orange-900 flex items-center gap-2">
              <Upload className="w-5 h-5" />
              PUSH : Portail Client → Truck Database
            </h4>
            <p className="text-sm text-orange-700 mt-1">
              Envoi des opérations et mises à jour de statut vers Truck Database
            </p>
          </div>

          {/* Sync Mode */}
          <div className="border rounded-lg p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Clock className="w-5 h-5 text-orange-500" />
              Mode de synchronisation PUSH
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { value: 'realtime', label: 'Temps réel', desc: 'Synchronisation immédiate à chaque événement', icon: Zap },
                { value: 'batch', label: 'Par lots', desc: 'Regroupement des syncs par intervalle', icon: Calendar },
                { value: 'manual', label: 'Manuel', desc: 'Synchronisation déclenchée manuellement', icon: Play }
              ].map(mode => (
                <label
                  key={mode.value}
                  className={`flex flex-col p-4 border rounded-lg cursor-pointer transition-all ${
                    config.push_config?.sync_mode === mode.value
                      ? 'border-orange-500 bg-orange-50 ring-2 ring-orange-200'
                      : 'border-gray-200 hover:border-orange-300'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <input
                      type="radio"
                      name="push_sync_mode"
                      className="w-4 h-4 text-orange-600"
                      checked={config.push_config?.sync_mode === mode.value}
                      onChange={() => updatePushConfig('sync_mode', mode.value)}
                    />
                    <mode.icon className={`w-5 h-5 ${config.push_config?.sync_mode === mode.value ? 'text-orange-600' : 'text-gray-400'}`} />
                    <span className="font-medium text-gray-900">{mode.label}</span>
                  </div>
                  <p className="text-sm text-gray-500 ml-7">{mode.desc}</p>
                </label>
              ))}
            </div>

            {config.push_config?.sync_mode === 'batch' && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Intervalle entre les lots (minutes)
                </label>
                <select
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  value={config.push_config?.batch_interval_minutes || 15}
                  onChange={(e) => updatePushConfig('batch_interval_minutes', parseInt(e.target.value))}
                >
                  <option value={5}>5 minutes</option>
                  <option value={10}>10 minutes</option>
                  <option value={15}>15 minutes</option>
                  <option value={30}>30 minutes</option>
                  <option value={60}>1 heure</option>
                </select>
              </div>
            )}
          </div>

          {/* Trigger Events */}
          <div className="border rounded-lg p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Événements déclencheurs</h3>
            <p className="text-sm text-gray-600">
              Sélectionnez les événements qui déclenchent une synchronisation vers Truck Database
            </p>

            <div className="space-y-3">
              {[
                { key: 'sync_on_create', label: 'Création d\'opération', desc: 'Synchroniser lors de la création d\'une nouvelle opération' },
                { key: 'sync_on_status_change', label: 'Changement de statut', desc: 'Synchroniser à chaque changement de statut' },
                { key: 'sync_on_update', label: 'Mise à jour', desc: 'Synchroniser à chaque modification d\'opération' }
              ].map(item => (
                <div key={item.key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">{item.label}</p>
                    <p className="text-sm text-gray-500">{item.desc}</p>
                  </div>
                  <input
                    type="checkbox"
                    className="w-5 h-5 text-orange-600 rounded"
                    checked={config.push_config?.[item.key] ?? true}
                    onChange={(e) => updatePushConfig(item.key, e.target.checked)}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Status Triggers */}
          <div className="border rounded-lg p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Statuts déclencheurs
            </h3>
            <p className="text-sm text-gray-600">
              Seuls ces statuts déclencheront une synchronisation vers Truck Database
            </p>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {availableStatuses.map((status) => (
                <label
                  key={status.value}
                  className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-all ${
                    config.sync_trigger_statuses?.includes(status.value)
                      ? 'border-orange-500 bg-orange-50'
                      : 'border-gray-200 hover:border-orange-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    className="w-4 h-4 text-orange-600 rounded"
                    checked={config.sync_trigger_statuses?.includes(status.value)}
                    onChange={() => handleStatusToggle(status.value)}
                  />
                  <span className="text-sm font-medium text-gray-700">{status.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Status Mapping */}
          <div className="border rounded-lg p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <ArrowRight className="w-5 h-5 text-purple-500" />
              Mapping des statuts
            </h3>

            <div className="overflow-hidden border rounded-lg">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Customer Portal</th>
                    <th className="px-4 py-3 text-center">→</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Truck Database</th>
                    <th className="px-4 py-3 w-20"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {Object.entries(config.status_mapping || {}).map(([cpStatus, truckStatus]) => (
                    <tr key={cpStatus} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {statusLabels[cpStatus] || cpStatus}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <ArrowRight className="w-4 h-4 text-gray-400 mx-auto" />
                      </td>
                      <td className="px-4 py-3">
                        {editingMapping === cpStatus ? (
                          <input
                            type="text"
                            className="w-full px-3 py-1 border border-blue-400 rounded text-sm"
                            defaultValue={truckStatus}
                            autoFocus
                            onBlur={(e) => handleMappingChange(cpStatus, e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleMappingChange(cpStatus, e.target.value);
                              if (e.key === 'Escape') setEditingMapping(null);
                            }}
                          />
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {truckStatus}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button onClick={() => setEditingMapping(cpStatus)} className="p-1 text-gray-400 hover:text-blue-600">
                          <Edit3 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Tab: PULL (Truck DB to Portal) */}
      {activeTab === 'pull' && (
        <div className="space-y-6">
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <h4 className="font-semibold text-purple-900 flex items-center gap-2">
              <Download className="w-5 h-5" />
              PULL : Truck Database → Portail Client
            </h4>
            <p className="text-sm text-purple-700 mt-1">
              Récupération des chauffeurs, véhicules, clients et routes depuis Truck Database
            </p>
          </div>

          {/* Sync Status Cards */}
          {syncStatus && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="border rounded-lg p-4 bg-white">
                <div className="flex items-center gap-3">
                  <Users className="w-8 h-8 text-blue-500" />
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{syncStatus.drivers?.cached_count || 0}</p>
                    <p className="text-sm text-gray-500">Chauffeurs</p>
                  </div>
                </div>
                <div className="mt-3 text-xs text-gray-400">
                  {syncStatus.drivers?.last_update ? `Sync: ${new Date(syncStatus.drivers.last_update).toLocaleString('fr-FR')}` : 'Non synchronisé'}
                </div>
                <button
                  onClick={() => triggerSync('drivers')}
                  disabled={syncingType === 'drivers'}
                  className="mt-2 w-full px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {syncingType === 'drivers' ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  Sync
                </button>
              </div>

              <div className="border rounded-lg p-4 bg-white">
                <div className="flex items-center gap-3">
                  <Truck className="w-8 h-8 text-green-500" />
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{syncStatus.vehicles?.cached_count || 0}</p>
                    <p className="text-sm text-gray-500">Véhicules</p>
                  </div>
                </div>
                <div className="mt-3 text-xs text-gray-400">
                  {syncStatus.vehicles?.last_update ? `Sync: ${new Date(syncStatus.vehicles.last_update).toLocaleString('fr-FR')}` : 'Non synchronisé'}
                </div>
                <button
                  onClick={() => triggerSync('vehicles')}
                  disabled={syncingType === 'vehicles'}
                  className="mt-2 w-full px-3 py-1.5 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {syncingType === 'vehicles' ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  Sync
                </button>
              </div>

              <div className="border rounded-lg p-4 bg-white">
                <div className="flex items-center gap-3">
                  <Route className="w-8 h-8 text-purple-500" />
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{syncStatus.routes?.cached_count || 0}</p>
                    <p className="text-sm text-gray-500">Routes</p>
                  </div>
                </div>
                <div className="mt-3 text-xs text-gray-400">
                  {syncStatus.routes?.cities_count || 0} villes disponibles
                </div>
                <button
                  onClick={() => triggerSync('routes')}
                  disabled={syncingType === 'routes'}
                  className="mt-2 w-full px-3 py-1.5 text-sm bg-purple-100 text-purple-700 rounded hover:bg-purple-200 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {syncingType === 'routes' ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  Sync
                </button>
              </div>

              <div className="border rounded-lg p-4 bg-white">
                <div className="flex items-center gap-3">
                  <Map className="w-8 h-8 text-orange-500" />
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{syncStatus.vehicle_types?.cached_count || 0}</p>
                    <p className="text-sm text-gray-500">Types véhicules</p>
                  </div>
                </div>
                <div className="mt-3 text-xs text-gray-400">
                  {syncStatus.vehicle_types?.mapping_count || 0} mappings
                </div>
              </div>
            </div>
          )}

          {/* Sync Mode */}
          <div className="border rounded-lg p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Clock className="w-5 h-5 text-purple-500" />
              Mode de synchronisation PULL
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { value: 'delta', label: 'Delta (Incrémental)', desc: 'Récupère uniquement les modifications', icon: Activity },
                { value: 'full', label: 'Complète', desc: 'Télécharge toutes les données', icon: Download },
                { value: 'manual', label: 'Manuel', desc: 'Synchronisation déclenchée manuellement', icon: Play }
              ].map(mode => (
                <label
                  key={mode.value}
                  className={`flex flex-col p-4 border rounded-lg cursor-pointer transition-all ${
                    config.pull_config?.sync_mode === mode.value
                      ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200'
                      : 'border-gray-200 hover:border-purple-300'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <input
                      type="radio"
                      name="pull_sync_mode"
                      className="w-4 h-4 text-purple-600"
                      checked={config.pull_config?.sync_mode === mode.value}
                      onChange={() => updatePullConfig('sync_mode', mode.value)}
                    />
                    <mode.icon className={`w-5 h-5 ${config.pull_config?.sync_mode === mode.value ? 'text-purple-600' : 'text-gray-400'}`} />
                    <span className="font-medium text-gray-900">{mode.label}</span>
                  </div>
                  <p className="text-sm text-gray-500 ml-7">{mode.desc}</p>
                </label>
              ))}
            </div>
          </div>

          {/* Sync Timing */}
          <div className="border rounded-lg p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Fréquence de synchronisation</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Durée du cache (minutes)
                </label>
                <select
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  value={config.pull_config?.cache_ttl_minutes || 5}
                  onChange={(e) => updatePullConfig('cache_ttl_minutes', parseInt(e.target.value))}
                >
                  <option value={1}>1 minute</option>
                  <option value={5}>5 minutes</option>
                  <option value={15}>15 minutes</option>
                  <option value={30}>30 minutes</option>
                  <option value={60}>1 heure</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Durée avant de déclencher une sync delta automatique
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sync complète (heures)
                </label>
                <select
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  value={config.pull_config?.full_sync_interval_hours || 24}
                  onChange={(e) => updatePullConfig('full_sync_interval_hours', parseInt(e.target.value))}
                >
                  <option value={6}>6 heures</option>
                  <option value={12}>12 heures</option>
                  <option value={24}>24 heures</option>
                  <option value={48}>48 heures</option>
                  <option value={168}>1 semaine</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Intervalle entre les synchronisations complètes
                </p>
              </div>
            </div>
          </div>

          {/* Data to Sync */}
          <div className="border rounded-lg p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Données à récupérer</h3>
            
            <div className="space-y-3">
              {[
                { key: 'sync_drivers', label: 'Chauffeurs', desc: 'Liste des chauffeurs Baobab actifs', icon: Users },
                { key: 'sync_vehicles', label: 'Véhicules', desc: 'Flotte de véhicules disponibles', icon: Truck },
                { key: 'sync_enterprise_clients', label: 'Clients entreprises', desc: 'Synchroniser les entreprises bidirectionnellement', icon: Database },
                { key: 'sync_routes', label: 'Routes pré-calculées', desc: 'Distances entre villes principales', icon: Route },
                { key: 'auto_calculate_distance', label: 'Calcul auto distance', desc: 'Calculer automatiquement la distance lors de la création', icon: Map }
              ].map(item => (
                <div key={item.key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <item.icon className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="font-medium text-gray-900">{item.label}</p>
                      <p className="text-sm text-gray-500">{item.desc}</p>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    className="w-5 h-5 text-purple-600 rounded"
                    checked={config.pull_config?.[item.key] ?? true}
                    onChange={(e) => updatePullConfig(item.key, e.target.checked)}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Tab: Distances */}
      {activeTab === 'distances' && (
        <div className="space-y-6">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h4 className="font-semibold text-green-900 flex items-center gap-2">
              <Route className="w-5 h-5" />
              Calcul de distances et GPS
            </h4>
            <p className="text-sm text-green-700 mt-1">
              Configuration des sources de données géographiques et calcul de distance
            </p>
          </div>

          {/* Distance Priority */}
          <div className="border rounded-lg p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Priorité de calcul de distance</h3>
            <p className="text-sm text-gray-600">
              Sélectionnez la source prioritaire pour le calcul des distances
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { 
                  value: 'osrm', 
                  label: 'OSRM (Recommandé)', 
                  desc: 'Service open-source gratuit, calcul dynamique pour toutes les routes',
                  pros: ['Gratuit', 'Routes mondiales', 'Calcul temps réel'],
                  color: 'green'
                },
                { 
                  value: 'truck_db', 
                  label: 'Base Truck DB', 
                  desc: 'Routes pré-calculées, réponse instantanée',
                  pros: ['Ultra rapide', '1167 routes CI', 'Hors-ligne'],
                  color: 'blue'
                },
                { 
                  value: 'mapbox', 
                  label: 'Mapbox', 
                  desc: 'API premium avec trafic temps réel',
                  pros: ['Trafic live', 'Haute précision', 'Routing avancé'],
                  color: 'purple'
                }
              ].map(option => (
                <label
                  key={option.value}
                  className={`flex flex-col p-4 border rounded-lg cursor-pointer transition-all ${
                    config.pull_config?.distance_priority === option.value
                      ? `border-${option.color}-500 bg-${option.color}-50 ring-2 ring-${option.color}-200`
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <input
                      type="radio"
                      name="distance_priority"
                      className={`w-4 h-4 text-${option.color}-600`}
                      checked={config.pull_config?.distance_priority === option.value}
                      onChange={() => updatePullConfig('distance_priority', option.value)}
                    />
                    <span className="font-medium text-gray-900">{option.label}</span>
                  </div>
                  <p className="text-sm text-gray-500 mb-2">{option.desc}</p>
                  <ul className="text-xs text-gray-600 space-y-1">
                    {option.pros.map((pro, i) => (
                      <li key={i}>✓ {pro}</li>
                    ))}
                  </ul>
                </label>
              ))}
            </div>
          </div>

          {/* Current Sources Info */}
          <div className="border rounded-lg p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Sources actuellement configurées</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 border rounded-lg bg-green-50">
                <h4 className="font-semibold text-green-900 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5" />
                  OpenStreetMap + OSRM
                </h4>
                <p className="text-sm text-green-700 mt-1">
                  Geocoding + Routage gratuits
                </p>
                <ul className="text-sm text-green-600 mt-2 space-y-1">
                  <li>✓ Toutes les villes du monde</li>
                  <li>✓ Calcul de route en temps réel</li>
                  <li>✓ Coordonnées GPS automatiques</li>
                  <li>✓ Trajets internationaux supportés</li>
                </ul>
              </div>

              <div className="p-4 border rounded-lg bg-blue-50">
                <h4 className="font-semibold text-blue-900 flex items-center gap-2">
                  <Database className="w-5 h-5" />
                  Base Truck Database
                </h4>
                <p className="text-sm text-blue-700 mt-1">
                  Routes pré-calculées (fallback)
                </p>
                <ul className="text-sm text-blue-600 mt-2 space-y-1">
                  <li>✓ 112 villes référencées</li>
                  <li>✓ 1167 routes pré-calculées</li>
                  <li>✓ Réponse instantanée</li>
                  <li>✓ Fonctionne hors-ligne</li>
                </ul>
              </div>
            </div>
          </div>

          {/* API Endpoints */}
          <div className="border rounded-lg p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Endpoints disponibles</h3>
            
            <div className="space-y-3 font-mono text-sm">
              <div className="p-3 bg-gray-50 rounded">
                <code className="text-green-600">GET /api/external/distance/estimate</code>
                <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs">PUBLIC</span>
                <p className="text-gray-500 mt-1 font-sans">Distance entre deux villes (OSRM prioritaire)</p>
              </div>
              <div className="p-3 bg-gray-50 rounded">
                <code className="text-blue-600">GET /api/external/distance</code>
                <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">AUTH</span>
                <p className="text-gray-500 mt-1 font-sans">Distance via Truck Database uniquement</p>
              </div>
              <div className="p-3 bg-gray-50 rounded">
                <code className="text-blue-600">GET /api/external/routes/cities</code>
                <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">AUTH</span>
                <p className="text-gray-500 mt-1 font-sans">Liste des villes pour autocomplétion</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab: History */}
      {activeTab === 'history' && (
        <div className="space-y-6">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 flex items-center gap-2">
              <History className="w-5 h-5" />
              Historique de synchronisation
            </h4>
            <p className="text-sm text-gray-600 mt-1">
              Journal des dernières synchronisations avec Truck Database
            </p>
          </div>

          {/* Sync History Table */}
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Date</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Type</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Direction</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Éléments</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Statut</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {syncHistory.length > 0 ? (
                  syncHistory.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {new Date(item.timestamp).toLocaleString('fr-FR')}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                          {item.type}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {item.direction === 'push' ? (
                          <span className="inline-flex items-center gap-1 text-orange-600">
                            <Upload className="w-4 h-4" /> PUSH
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-purple-600">
                            <Download className="w-4 h-4" /> PULL
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                        {item.count || 0}
                      </td>
                      <td className="px-4 py-3">
                        {item.success ? (
                          <span className="inline-flex items-center gap-1 text-green-600">
                            <CheckCircle2 className="w-4 h-4" /> Succès
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-red-600">
                            <XCircle className="w-4 h-4" /> Échec
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                      Aucun historique de synchronisation disponible
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Refresh Button */}
          <div className="flex justify-end">
            <button
              onClick={fetchSyncHistory}
              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
            >
              <RefreshCw className="w-4 h-4" />
              Actualiser
            </button>
          </div>
        </div>
      )}

      {/* Documentation Link */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
          <ExternalLink className="w-4 h-4" />
          Documentation
        </h4>
        <p className="text-sm text-gray-600">
          Consultez <code className="bg-gray-200 px-1 rounded">/app/docs/TRUCK_DATABASE_INTEGRATION.md</code> pour la documentation complète.
        </p>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4 pt-4 border-t sticky bottom-0 bg-white py-4">
        <button
          onClick={saveConfig}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
        >
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
          Enregistrer la configuration
        </button>

        <button
          onClick={testConnection}
          disabled={testing || !config.api_url || !config.api_key}
          className="flex items-center gap-2 px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium"
        >
          {testing ? <Loader2 className="w-5 h-5 animate-spin" /> : <RefreshCw className="w-5 h-5" />}
          Tester la connexion
        </button>
      </div>
    </div>
  );
};

export default TruckDatabaseConfigPanel;
